import { Download, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAdminViewModel } from "@/pages/dashboard/admin/AdminViewModelContext";
import { getLastNDaysRange } from "@/lib/time";

export function ReportFiltersView() {
  const {
    user,
    queryClient,
    isAdmin,
    isManagerOnly,
    isReadonlyAdmin,
    isDualManagerReadonly,
    canViewReports,
    canEditPunches,
    managedEditUserIds,
    filterCostCode,
    setFilterCostCode,
    filterCustomer,
    setFilterCustomer,
    filterEmployee,
    setFilterEmployee,
    filterCostCodeSearch,
    setFilterCostCodeSearch,
    filterCostCodeOpen,
    setFilterCostCodeOpen,
    filterCostCodeRef,
    filterCustomerSearch,
    setFilterCustomerSearch,
    filterCustomerOpen,
    setFilterCustomerOpen,
    filterCustomerRef,
    filterEmployeeSearch,
    setFilterEmployeeSearch,
    filterEmployeeOpen,
    setFilterEmployeeOpen,
    filterEmployeeRef,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    entriesSearch,
    setEntriesSearch,
    entriesPage,
    setEntriesPage,
    entriesPageSize,
    setEntriesPageSize,
    exportError,
    setExportError,
    filterCostCodesData,
    filterCustomersData,
    filterEmployeesData,
    entriesLoading,
    entriesCountData,
    correctionStats,
    correctionStatsLoading,
    filteredEntries,
    chartData,
    handleExport,
  } = useAdminViewModel();

  return (
                <Card>
                  <CardHeader>
                    <CardTitle>Report Filters</CardTitle>
                    <CardDescription>Search, filter by employee, cost code, customer/company, or dates, and export real backend records.</CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-wrap gap-3">
                    <div className="relative min-w-[200px] flex-[2]">
                      <Search className="pointer-events-none absolute left-3 top-3 size-4 text-muted-foreground" />
                      <Input className="pl-9" value={entriesSearch} onChange={(event) => { setEntriesSearch(event.target.value); setEntriesPage(0); }} placeholder="Search reports" />
                    </div>
                    {/* Searchable employee filter for admin report */}
                    <div className="relative min-w-[160px] flex-1" ref={filterEmployeeRef}>
                      <Input
                        value={filterEmployeeOpen ? filterEmployeeSearch : (filterEmployee === "all" ? "" : (filterEmployeesData ?? []).find((u) => String(u.id) === filterEmployee)?.full_name || "")}
                        onChange={(e) => {
                          setFilterEmployeeSearch(e.target.value);
                          if (!filterEmployeeOpen) setFilterEmployeeOpen(true);
                        }}
                        onFocus={() => {
                          setFilterEmployeeSearch("");
                          setFilterEmployeeOpen(true);
                        }}
                        placeholder={filterEmployeeOpen ? "Search employees..." : "All employees"}
                      />
                      {filterEmployeeOpen && (
                        <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover shadow-lg">
                          <button
                            type="button"
                            className="w-full text-left cursor-pointer px-3 py-2 text-sm hover:bg-muted bg-transparent border-0"
                            onClick={() => {
                              setFilterEmployee("all");
                              setFilterEmployeeOpen(false);
                              setFilterEmployeeSearch("");
                              setEntriesPage(0);
                            }}
                          >
                            All employees
                          </button>
                          {(filterEmployeesData ?? []).map((u) => (
                            <button
                              type="button"
                              key={u.id}
                              className="w-full text-left cursor-pointer px-3 py-2 text-sm hover:bg-muted bg-transparent border-0"
                              onClick={() => {
                                setFilterEmployee(String(u.id));
                                setFilterEmployeeOpen(false);
                                setFilterEmployeeSearch("");
                                setEntriesPage(0);
                              }}
                            >
                              {u.full_name}{u.company_name ? ` (${u.company_name})` : ""}
                            </button>
                          ))}
                          {(filterEmployeesData ?? []).length === 0 && (
                            <div className="px-3 py-2 text-sm text-muted-foreground">No results.</div>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Searchable cost code filter for admin */}
                    <div className="relative min-w-[160px] flex-1" ref={filterCostCodeRef}>
                      <Input
                        value={filterCostCodeOpen ? filterCostCodeSearch : (filterCostCode === "all" ? "" : (filterCostCodesData ?? []).find((cc) => String(cc.id) === filterCostCode)?.cost_code || "")}
                        onChange={(e) => {
                          setFilterCostCodeSearch(e.target.value);
                          if (!filterCostCodeOpen) setFilterCostCodeOpen(true);
                        }}
                        onFocus={() => {
                          setFilterCostCodeSearch("");
                          setFilterCostCodeOpen(true);
                        }}
                        placeholder={filterCostCodeOpen ? "Search cost codes..." : "All cost codes"}
                      />
                      {filterCostCodeOpen && (
                        <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover shadow-lg">
                          <button
                            type="button"
                            className="w-full text-left cursor-pointer px-3 py-2 text-sm hover:bg-muted bg-transparent border-0"
                            onClick={() => {
                              setFilterCostCode("all");
                              setFilterCostCodeOpen(false);
                              setFilterCostCodeSearch("");
                              setEntriesPage(0);
                            }}
                          >
                            All cost codes
                          </button>
                          {(filterCostCodesData ?? []).map((cc) => (
                            <button
                              type="button"
                              key={cc.id}
                              className="w-full text-left cursor-pointer px-3 py-2 text-sm hover:bg-muted bg-transparent border-0"
                              onClick={() => {
                                setFilterCostCode(String(cc.id));
                                setFilterCostCodeOpen(false);
                                setFilterCostCodeSearch("");
                                setEntriesPage(0);
                              }}
                            >
                              {cc.cost_code}
                            </button>
                          ))}
                          {(filterCostCodesData ?? []).length === 0 && (
                            <div className="px-3 py-2 text-sm text-muted-foreground">No results.</div>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Searchable customer filter for admin */}
                    <div className="relative min-w-[160px] flex-1" ref={filterCustomerRef}>
                      <Input
                        value={filterCustomerOpen ? filterCustomerSearch : (filterCustomer === "all" ? "" : (filterCustomersData ?? []).find((c) => String(c.id) === filterCustomer)?.company || "")}
                        onChange={(e) => {
                          setFilterCustomerSearch(e.target.value);
                          if (!filterCustomerOpen) setFilterCustomerOpen(true);
                        }}
                        onFocus={() => {
                          setFilterCustomerSearch("");
                          setFilterCustomerOpen(true);
                        }}
                        placeholder={filterCustomerOpen ? "Search customers..." : "All customers"}
                      />
                      {filterCustomerOpen && (
                        <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover shadow-lg">
                          <button
                            type="button"
                            className="w-full text-left cursor-pointer px-3 py-2 text-sm hover:bg-muted bg-transparent border-0"
                            onClick={() => {
                              setFilterCustomer("all");
                              setFilterCustomerOpen(false);
                              setFilterCustomerSearch("");
                              setEntriesPage(0);
                            }}
                          >
                            All customers
                          </button>
                          {(filterCustomersData ?? []).map((c) => (
                            <button
                              type="button"
                              key={c.id}
                              className="w-full text-left cursor-pointer px-3 py-2 text-sm hover:bg-muted bg-transparent border-0"
                              onClick={() => {
                                setFilterCustomer(String(c.id));
                                setFilterCustomerOpen(false);
                                setFilterCustomerSearch("");
                                setEntriesPage(0);
                              }}
                            >
                              {c.company}
                            </button>
                          ))}
                          {(filterCustomersData ?? []).length === 0 && (
                            <div className="px-3 py-2 text-sm text-muted-foreground">No results.</div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => { setExportError(null); const r = getLastNDaysRange(7); setFromDate(r.from); setToDate(r.to); setEntriesPage(0); }}>Week</Button>
                      <Button size="sm" variant="outline" onClick={() => { setExportError(null); const r = getLastNDaysRange(30); setFromDate(r.from); setToDate(r.to); setEntriesPage(0); }}>Month</Button>
                    </div>
                    <Input className="min-w-[160px] flex-1" type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
                    <Input className="min-w-[160px] flex-1" type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} />
                    <div className="flex items-end">
                      <Button type="button" onClick={handleExport}>
                        <Download className="size-4" />
                        CSV
                      </Button>
                    </div>
                  </CardContent>
                  <div className="-mt-2 mb-2 flex justify-end px-2">
                    <Button variant="ghost" size="sm" onClick={() => { setExportError(null); setEntriesSearch(""); setFilterCostCode("all"); setFilterCustomer("all"); setFilterEmployee("all"); setFromDate(""); setToDate(""); setEntriesPage(0); setFilterCostCodeSearch(""); setFilterCustomerSearch(""); setFilterEmployeeSearch(""); setFilterCostCodeOpen(false); setFilterCustomerOpen(false); setFilterEmployeeOpen(false); }}>Clear filters</Button>
                  </div>
                  {exportError && (
                    <div className="px-2 mb-2 text-xs text-destructive">
                      {exportError}
                    </div>
                  )}
                </Card>
  );
}
